import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import LoadingSpinner from "./LoadingSpinner";

const API_BASE = process.env.REACT_APP_API_URL || "";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

async function readError(res) {
  try {
    const text = await res.text();
    try {
      const j = JSON.parse(text);
      return j?.error || j?.message || text || `HTTP ${res.status}`;
    } catch {
      return text || `HTTP ${res.status}`;
    }
  } catch {
    return `HTTP ${res.status}`;
  }
}

function formatTime(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

function pickRewardText(reward) {
  if (!reward) return "Your prize has been added.";

  const parts = [];

  if (Number(reward?.sparks || 0) > 0) {
    parts.push(`${reward.sparks} Sparks`);
  }

  if (Number(reward?.runes || 0) > 0) {
    parts.push(`${reward.runes} Runes`);
  }

  if (reward?.item) {
    parts.push("Inventory Item");
  }

  return parts.length ? parts.join(" + ") : "Your prize has been added.";
}

function SimpleModal({ open, title = "Hey!", children, onClose, footer }) {
  return (
    <AnimatePresence>
      {open && (
        <div
          className="modal_box"           
        >
        <div className="modal_title">Hey<button className="modal_close" onClick={onClose}/></div>
          <motion.div
            className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
          >
          <div className="modal_inside">
            <div className="shadow2" 
              style={{ fontWeight: 600, fontSize: 28, marginBottom: 6 }}
            >
              {title}
            </div>

            <div className="info"
              style={{
                fontSize: 21,
              }}
            >
              {children}
            </div>
</div>
            <div className="modal_footer">
              {footer ?? <button className="game-button red-button" onClick={onClose}>CLOSE</button>}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function MemoryCard({
  card,
  disabled,
  cardBackImageUrl,
  onFlip,
  index,
  cols,
}) {
  const isRevealed = Boolean(card?.revealed || card?.matched);
  const isMatched = Boolean(card?.matched);
  const img = card?.imageUrl || "";
  const label = card?.label || "";

  const cardMin = cols >= 6 ? 42 : cols >= 5 ? 48 : 58;

  return (
    <motion.button
      type="button"
      disabled={disabled || isMatched || isRevealed}
      onClick={() => onFlip(index)}
      whileTap={{ scale: disabled ? 1 : 0.94 }}
      animate={{
        scale: isMatched ? 0.94 : 1,
        opacity: isMatched ? 0.72 : 1,
      }}
      style={{
        position: "relative",
        width: "100%",
        minHeight: cardMin,
        aspectRatio: "1 / 1.08",
        border: 0,
        padding: 0,
        background: "transparent",
        perspective: 800,
        cursor: disabled || isMatched || isRevealed ? "default" : "pointer",
      }}
    >
      <motion.div
        animate={{ rotateY: isRevealed ? 180 : 0 }}
        transition={{ duration: 0.36, ease: [0.2, 0.8, 0.2, 1] }}
        style={{
          position: "absolute",
          inset: 0,
          transformStyle: "preserve-3d",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            borderRadius: 10,
            overflow: "hidden",
            border: "2px solid rgba(80,38,11,0.75)",
            boxShadow:
              "inset 0 3px 0 rgba(255,238,177,0.35), inset 0 -4px 0 rgba(57,24,6,0.35), 0 5px 0 rgba(61,25,7,0.65)",
            background:
              "linear-gradient(180deg, #bf7b38 0%, #8b4f22 50%, #663315 100%)",
          }}
        >
          {cardBackImageUrl ? (
            <img
              src={cardBackImageUrl}
              alt=""
              draggable={false}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                opacity: 0.95,
              }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "grid",
                placeItems: "center",
                background:
                  "radial-gradient(circle at 50% 35%, rgba(255,218,126,0.35), rgba(0,0,0,0) 48%), linear-gradient(180deg, #bb7838, #7a421c)",
              }}
            >
              <div
                style={{
                  color: "rgba(58,24,6,0.48)",
                  fontSize: cols >= 6 ? 24 : 32,
                  fontWeight: 900,
                  textShadow: "1px 1px rgba(255,225,151,0.32)",
                }}
              >
                ☥
              </div>
            </div>
          )}

          <div
            style={{
              position: "absolute",
              inset: 5,
              borderRadius: 8,
              border: "1px solid rgba(255,220,137,0.32)",
              pointerEvents: "none",
            }}
          />
        </div>

        <div
          style={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            borderRadius: 10,
            overflow: "hidden",
            border: isMatched
              ? "2px solid rgba(150,255,159,0.9)"
              : "2px solid rgba(255,229,144,0.86)",
            boxShadow: isMatched
              ? "0 0 18px rgba(115,255,128,0.48), 0 5px 0 rgba(22,75,28,0.7)"
              : "0 0 18px rgba(255,207,86,0.32), 0 5px 0 rgba(82,36,8,0.7)",
            background:
              "linear-gradient(180deg, #ffe1a0 0%, #b86a22 72%, #6e3511 100%)",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 4,
              borderRadius: 8,
              background:
                "radial-gradient(circle at 50% 34%, rgba(255,255,255,0.35), rgba(255,255,255,0) 42%), linear-gradient(180deg, #a55e25, #6d3614)",
              display: "grid",
              placeItems: "center",
              padding: 5,
            }}
          >
            {img ? (
              <img
                src={img}
                alt={label}
                draggable={false}
                style={{
                  width: "78%",
                  height: "78%",
                  objectFit: "contain",
                  filter:
                    "drop-shadow(0 5px 0 rgba(0,0,0,0.25)) drop-shadow(0 0 8px rgba(255,231,150,0.35))",
                }}
              />
            ) : (
              <div
                style={{
                  color: "#ffe8aa",
                  fontSize: cols >= 6 ? 24 : 34,
                  textShadow: "2px 2px rgba(0,0,0,0.45)",
                }}
              >
                🪬
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {isMatched && (
        <motion.div
          initial={{ scale: 0.3, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{
            position: "absolute",
            right: -4,
            top: -6,
            width: 24,
            height: 24,
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
            color: "#143b0d",
            background: "#a8ff8b",
            border: "2px solid #fff6bf",
            fontSize: 14,
            fontWeight: 900,
            boxShadow: "0 3px 0 rgba(0,0,0,0.32)",
          }}
        >
          ✓
        </motion.div>
      )}
    </motion.button>
  );
}

export default function MemoryGame({
  user,
  setUser,
  onNavigate,
  onClose,
  soundEnabled,
  sfxEnabled,
  sfxVolume,
}) {
  const audioCtxRef = useRef(null);

  const [starting, setStarting] = useState(false);
  const [flipping, setFlipping] = useState(false);
  const [error, setError] = useState("");

  const [config, setConfig] = useState(null);
  const [session, setSession] = useState(null);
  const [cards, setCards] = useState([]);
  const [status, setStatus] = useState("idle");

  const [expiresAt, setExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [attempts, setAttempts] = useState(0);

  const [modalQueue, setModalQueue] = useState([]);
  const [activeModal, setActiveModal] = useState(null);

  useEffect(() => {
  const imagesToPreload = [
    "/memory1.png",
    "/memory2.png",
    "/memory3.png",
    "/memory4.png",
    "/memory5.png",
    "/memory6.png",
    "/memory7.png",
    "/memory8.png",
    "/memory9.png",
    "/memory10.png",
    "/memory11.png",
    "/memory12.png",
    "/memory13.png",
    "/memory14.png",
    "/memory15.png",
  ];

  imagesToPreload.forEach((src) => {
    const img = new Image();
    img.src = src;
  });
}, []);

  const enqueueModal = useCallback((item) => {
    setModalQueue((q) => [...q, item]);
  }, []);

  useEffect(() => {
    if (!activeModal && modalQueue.length > 0) {
      setActiveModal(modalQueue[0]);
      setModalQueue((q) => q.slice(1));
    }
  }, [modalQueue, activeModal]);

  const closeActiveModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const sfxGain = useMemo(() => {
    if (!sfxEnabled) return 0;
    return clamp(Number(sfxVolume ?? 1), 0, 1);
  }, [sfxEnabled, sfxVolume]);

  const playTone = useCallback(
    (kind = "tap") => {
      if (!soundEnabled || !sfxEnabled || sfxGain <= 0) return;

      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext ||
            window.webkitAudioContext)();
        }

        const ctx = audioCtxRef.current;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        const t = ctx.currentTime;
        const freq =
          kind === "match" ? 880 : kind === "win" ? 1180 : kind === "bad" ? 180 : 520;

        osc.type = kind === "bad" ? "sawtooth" : "triangle";
        osc.frequency.setValueAtTime(freq, t);
        osc.frequency.exponentialRampToValueAtTime(
          kind === "bad" ? 120 : freq * 1.15,
          t + 0.12
        );

        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.18 * sfxGain, t + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(t);
        osc.stop(t + 0.17);
      } catch {}
    },
    [soundEnabled, sfxEnabled, sfxGain]
  );

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("token");
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-TG-INITDATA": window.Telegram?.WebApp?.initData || "",
    };
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/me`, {
        headers: authHeaders(),
      });

      if (!res.ok) return;

      const me = await res.json();
      if (me && typeof setUser === "function") {
        setUser(me);
      }
    } catch {}
  }, [authHeaders, setUser]);

  const secondsLeft = useMemo(() => {
    if (!expiresAt) return 0;
    const t = Date.parse(expiresAt);
    if (!Number.isFinite(t)) return 0;
    return Math.max(0, Math.ceil((t - now) / 1000));
  }, [expiresAt, now]);

  const rows = Number(config?.gridRows || session?.gridRows || 6);
  const cols = Number(config?.gridCols || session?.gridCols || 5);
  const totalCards = Math.max(0, rows * cols);
  const maxAttempts = Number(config?.maxAttempts || session?.maxAttempts || 0);
  const matchedCount = cards.filter((c) => c?.matched).length;
  const progress = totalCards > 0 ? matchedCount / totalCards : 0;

  const hasActiveBoard = status === "active" && cards.length > 0;

  const boardWidth = useMemo(() => {
    if (cols >= 6) return "min(calc(100vw - 46px), 350px)";
    if (cols === 5) return "min(calc(100vw - 46px), 338px)";
    return "min(calc(100vw - 46px), 320px)";
  }, [cols]);

 const startGame = useCallback(async (confirmPayment = false) => {
  if (starting) return;

  setStarting(true);
  setError("");
  setActiveModal(null);

  try {
    const res = await fetch(`${API_BASE}/api/memory-game/start`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify({ confirmPayment }),
    });

    const json = await res.json().catch(() => ({}));

    if (!res.ok) {

if (json?.error === "CONFIRM_PAYMENT_REQUIRED") {
  enqueueModal({
    type: "CONFIRM_PAYMENT",
    payload: {
      message:
        json?.message ||
        `This round costs ${json?.entryFeeSparks || 0} sparks + ${json?.entryFeeRunes || 0} runes.`,
      entryFeeSparks: json?.entryFeeSparks || 0,
      entryFeeRunes: json?.entryFeeRunes || 0,
    },
  });

  return;
}

      const code = json?.error || json?.message || `HTTP_${res.status}`;

      let message = "Unable to start Memory Game right now.";

      if (code === "Memory game is disabled") {
        message = "Memory Game is not available right now.";
      } else if (code === "Daily memory game limit reached") {
        message = "You reached your daily Memory Game limit.";
      } else if (code === "You already have an active memory game") {
        message = "You already have an active Memory Game round.";
      } else if (code === "Not enough sparks") {
  message = "You don’t have enough Sparks to enter.";
} else if (code === "Not enough runes") {
  message = "You don’t have enough Runes to enter.";
} else if (typeof code === "string" && code.trim()) {
        message = code;
      }

      enqueueModal({
        type: "INFO",
        payload: { message },
      });

      return;
    }

    setSession({
      id: json.sessionId,
      gridRows: json.gridRows,
      gridCols: json.gridCols,
      maxAttempts: json.maxAttempts,
      expiresAt: json.expiresAt,
      status: "active",
    });

    setConfig((prev) => ({
      ...(prev || {}),
      enabled: true,
      gridRows: json.gridRows,
      gridCols: json.gridCols,
      totalCards: json.totalCards,
      maxAttempts: json.maxAttempts,
      roundDurationSeconds: json.roundDurationSeconds,
      cardBackImageUrl: json.cardBackImageUrl || prev?.cardBackImageUrl || "",
    }));

    setStatus("active");
    setAttempts(Number(json.attempts || 0));
    setExpiresAt(json.expiresAt);
    setCards(Array.isArray(json.cards) ? json.cards : []);
    playTone("tap");

    if (json?.user) {
      setUser?.((prev) => ({
        ...(prev || user || {}),
        runes: json.user.runes ?? prev?.runes,
        sparks: json.user.sparks ?? prev?.sparks,
      }));
    } else {
      await refreshMe();
    }
  } catch (e) {
    enqueueModal({
      type: "INFO",
      payload: {
        message: e?.message || "Failed to start Memory Game.",
      },
    });
  } finally {
    setStarting(false);
  }
}, [starting, authHeaders, enqueueModal, playTone, setUser, user, refreshMe]);

  const clearTemporaryPair = useCallback(() => {
    setCards((prev) => {
      const revealedUnmatched = prev.filter((c) => c?.revealed && !c?.matched);
      if (revealedUnmatched.length < 2) return prev;

      return prev.map((c) =>
        c?.revealed && !c?.matched
          ? { index: c.index, revealed: false, matched: false }
          : c
      );
    });
  }, []);

  const flipCard = useCallback(
    async (index) => {
      if (flipping || status !== "active") return;

      const current = cards[index];
      if (!current || current.matched || current.revealed) return;

      const revealedUnmatched = cards.filter((c) => c?.revealed && !c?.matched);
      if (revealedUnmatched.length >= 2) {
        clearTemporaryPair();
      }

      setFlipping(true);
      setError("");
      playTone("tap");

      try {
        const res = await fetch(`${API_BASE}/api/memory-game/flip`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({ index }),
        });

        if (!res.ok) throw new Error(await readError(res));

        const json = await res.json();

        const matchedSet = new Set(json?.matchedIndexes || []);
        const revealedMap = new Map();

        for (const c of json?.revealedCards || []) {
          revealedMap.set(Number(c.index), c);
        }

        if (json?.flipped) {
          revealedMap.set(Number(json.flipped.index), json.flipped);
        }

        setAttempts(Number(json?.attempts || 0));
        setStatus(json?.status || "active");

        setCards((prev) =>
          prev.map((card, i) => {
            const revealedCard = revealedMap.get(i);
            const isMatched = matchedSet.has(i);

            if (revealedCard) {
              return {
                ...card,
                ...revealedCard,
                revealed: true,
                matched: isMatched || Boolean(revealedCard.matched),
              };
            }

            if (isMatched) {
              return {
                ...card,
                matched: true,
                revealed: false,
              };
            }

            const stillRevealed = (json?.revealedIndexes || []).includes(i);

            return {
              ...card,
              revealed: stillRevealed,
              matched: false,
            };
          })
        );

        const beforeMatched = cards.filter((c) => c?.matched).length;
        const afterMatched = (json?.matchedIndexes || []).length;

        if (afterMatched > beforeMatched) {
          playTone("match");
        }

        if (json?.status === "completed") {
  playTone("win");

  if (json?.user) {
    setUser?.((prev) => ({
      ...(prev || user || {}),
      runes: json.user.runes ?? prev?.runes,
      sparks: json.user.sparks ?? prev?.sparks,
    }));
  } else {
    await refreshMe();
  }

          setTimeout(() => {
            enqueueModal({
              type: "WIN",
              payload: {
                reward: json?.reward,
                rewardText: pickRewardText(json?.reward),
              },
            });
          }, 380);
        }

        if (json?.status === "failed") {
          playTone("bad");

          setTimeout(() => {
            enqueueModal({
              type: "FAILED",
              payload: {
                message: "The ancient board reset before you solved every pair.",
              },
            });
          }, 380);
        }
      } catch (e) {
        const msg = e?.message || "Failed to flip card.";
        setError(msg);

        enqueueModal({
          type: "INFO",
          payload: { message: msg },
        });
      } finally {
        setTimeout(() => setFlipping(false), 180);
      }
    },
    [
  flipping,
  status,
  cards,
  clearTemporaryPair,
  playTone,
  authHeaders,
  refreshMe,
  enqueueModal,
  setUser,
  user,
]
  );

  useEffect(() => {
    if (status !== "active") return;
    if (!expiresAt) return;
    if (secondsLeft > 0) return;

    setStatus("expired");

    enqueueModal({
      type: "FAILED",
      payload: {
        message: "The temple door closed. Try another round.",
      },
    });
  }, [secondsLeft, status, expiresAt, enqueueModal]);

  const goBack = useCallback(() => {
    if (typeof onNavigate === "function") {
      onNavigate("arenas");
      return;
    }

    onClose?.();
  }, [onNavigate, onClose]);

  const resetRound = useCallback(() => {
    setCards([]);
    setSession(null);
    setStatus("idle");
    setAttempts(0);
    setExpiresAt(null);
    setError("");
  }, []);

  return (
  <div className="shop-container">
    <div
  style={{
    fontFamily: '"Fredoka", sans-serif',
    fontWeight: 600,
    width: "100%",
    marginTop: -15,
    padding: hasActiveBoard ? "0px 15px 15px" : "0px 15px 15px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxSizing: "border-box",
  }}
>
        <motion.div
          initial={{ opacity: 0, y: -18 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            width: "min(96vw, 430px)",
            textAlign: "center",
            marginTop: 0,
          }}
        >
          <div
          className="shadow2"
            style={{
              fontSize: 32,
              lineHeight: "42px",
              color: "#fff1b6",
            }}
          >
            Memory Tomb
          </div>

          <div
          className="shadow1"
            style={{
              color: "#fff",
              fontSize: 18,
              marginTop: 10,
              marginBottom: 10,
              display: "inline-block"
            }}
          >
            Uncover the ancient symbols and match every pair before the tomb seals again.
          </div>
        </motion.div>

{hasActiveBoard && (
  <>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.08 }}
          style={{
            width: "min(96vw, 430px)",
            marginTop: 0,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 5,
          }}
        >
          <div
            style={{
              borderRadius: 10,
              padding: "8px 10px",
              background: "rgba(48,22,7,0.75)",
              boxShadow: "inset 0 1px rgba(255,255,255,0.12)",
              textAlign: "center",
              color: "#fff",
            }}
          >
            <div style={{ fontSize: 15, opacity: 0.72 }}>TIME</div>
            <div
              style={{
                fontSize: 21,
                color:
                  secondsLeft <= 10 && hasActiveBoard ? "#ff8b73" : "#fff5c9",
              }}
            >
              {hasActiveBoard ? formatTime(secondsLeft) : "--:--"}
            </div>
          </div>

          <div
            style={{
              borderRadius: 10,
              padding: "8px 10px",
              background: "rgba(48,22,7,0.75)",
              boxShadow: "inset 0 1px rgba(255,255,255,0.12)",
              textAlign: "center",
              color: "#fff"
            }}
          >
            <div style={{ fontSize: 15, opacity: 0.72 }}>TRIES</div>
            <div
              style={{
                fontSize: 21,
                color: "#fff5c9",
              }}
            >
              {attempts}/{maxAttempts || "--"}
            </div>
          </div>

          <div
            style={{
              borderRadius: 10,
              padding: "8px 10px",
              background: "rgba(48,22,7,0.75)",
              boxShadow: "inset 0 1px rgba(255,255,255,0.12)",
              textAlign: "center",
              color: "#fff"
            }}
          >
            <div style={{ fontSize: 15, opacity: 0.72 }}>MATCHED</div>
            <div
              style={{
                fontSize: 21,
                color: "#fff5c9",
              }}
            >
              {matchedCount}/{totalCards || "--"}
            </div>
          </div>
        </motion.div>

        <div
          style={{
            width: "min(96vw, 430px)",
            marginTop: 10,
            height: 13,
            padding: 2,
            borderRadius: 999,
            background: "rgba(38,15,4,0.82)",
            border: "1px solid rgba(255,215,115,0.3)",
            boxShadow: "inset 0 2px 5px rgba(0,0,0,0.45)",
          }}
        >


          <motion.div
            animate={{ width: `${Math.round(progress * 100)}%` }}
            style={{
              height: "100%",
              borderRadius: 999,
              background:
                "linear-gradient(90deg, #5eff89, #ffe16a 58%, #ffb03a)",
              boxShadow: "0 0 12px rgba(255,210,92,0.55)",
            }}
          />
        </div>

         </>
)}

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16 }}
          style={{
            width: boardWidth,
            marginTop: 18,
            position: "relative",
            padding: 6,
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 16,
            borderRadius: 26,
            background:
              "linear-gradient(180deg, #d09243 0%, #a85f23 9%, #5b2b0d 100%)",
            boxShadow:
              "0 1px 0 rgba(44,18,5,0.72), 0 28px 48px rgba(0,0,0,0.45), inset 0 0 0 3px rgba(255,219,129,0.42), inset 0 0 0 8px rgba(99,43,12,0.55)",
            border: "3px solid rgba(255,214,116,0.75)",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: -14,
              width: 34,
              height: 34,
              transform: "translateX(-50%)",
              borderRadius: 999,
              background:
                "radial-gradient(circle at 35% 30%, #fff5b1, #d78b25 65%, #62300a)",
              border: "2px solid rgba(255,244,192,0.72)",
              boxShadow: "0 7px 0 rgba(0,0,0,0.25)",
              zIndex: 2,
            }}
          />

          <div
            style={{
              height: 30,
              margin: "-1px 34px 10px",
              background:
                "linear-gradient(180deg, rgba(255,235,158,0.9), rgba(188,105,27,0.3))",
              clipPath:
                "polygon(0 100%, 14% 20%, 42% 20%, 50% 0, 58% 20%, 86% 20%, 100% 100%)",
              opacity: 0.85,
            }}
          />

          {!hasActiveBoard ? (
            <div
              style={{
                minHeight: 350,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                padding: 22,
              }}
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 2.1 }}
                style={{
                  fontSize: 78,
                  filter: "drop-shadow(0 10px 0 rgba(0,0,0,0.26))",
                }}
              >
                🏛️
              </motion.div>

              <div
                style={{
                  fontSize: 28,
                  color: "#fff0bd",
                  textShadow: "2px 2px rgba(0,0,0,0.6)",
                }}
              >
                Enter the Tomb
              </div>

              <div
                style={{
                  fontSize: 15,
                  maxWidth: 300,
                  marginTop: 8,
                  color: "#fff",
                  lineHeight: 1.35,
                }}
              >
                Match every pair before time runs out.
              </div>

              <button
  className="game-button green-button"
  onClick={() => startGame(false)}
  disabled={starting}
  style={{
    marginTop: 26,
    width: 178,
    height: 66,
    border: 0,
    color: "#fff",
    fontSize: 24,
    cursor: starting ? "wait" : "pointer",
    opacity: starting ? 0.72 : 1,
  }}
>
  {starting ? "Opening…" : "Start"}
</button>
            </div>
                    ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  gap: cols >= 6 ? 4 : 5,
                }}
              >
                {cards.map((card, index) => (
                  <MemoryCard
                    key={index}
                    index={index}
                    card={card}
                    cols={cols}
                    disabled={flipping || status !== "active" || secondsLeft <= 0}
                    cardBackImageUrl={config?.cardBackImageUrl}
                    onFlip={flipCard}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={resetRound}
                style={{
                  width: "100%",
                  marginTop: 18,
                  height: 46,
                  border: "2px solid rgba(255, 220, 137, 0.45)",
                  borderRadius: 16,
                  background:
                    "linear-gradient(180deg, rgba(89,38,12,0.95), rgba(46,18,5,0.95))",
                  color: "#fff1b6",
                  fontSize: 15,
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textShadow: "2px 2px rgba(0,0,0,0.55)",
                  boxShadow:
                    "inset 0 2px 0 rgba(255,255,255,0.15), 0 6px 0 rgba(35,13,3,0.65)",
                  cursor: "pointer",
                }}
              >
                CLOSE THE TOMB
              </button>
            </>
          )}
        </motion.div>

        {error && (
          <div
            style={{
              width: "min(94vw, 390px)",
              marginTop: 18,
              padding: "10px 12px",
              borderRadius: 14,
              color: "#ffe1d7",
              background: "rgba(90, 0, 0, 0.42)",
              border: "1px solid rgba(255,130,99,0.35)",
              textAlign: "center",
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}
      </div>


{activeModal?.type === "CONFIRM_PAYMENT" && (
  <div className="modal_box">
  <div className="modal_title">Confirm<button onClick={closeActiveModal} className="modal_close"/></div>
    <motion.div
      className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
      <div className="modal_inside">
      <img
        src="/finger.png"
        alt="Confirm"
        style={{
          height: "80px",
          marginBottom: 10
        }}
      />

      <div className="shadow2"  style={{ fontWeight: 600, fontSize: 28,marginBottom: "10px" }}>
        Enter The Tomb?
      </div>

      <div className="info"
        
      >
        This round costs:
        <br />

        <div
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    marginTop: "10px",
    marginBottom: "8px",
    color: "#ffbb01",
    fontSize: "30px",
    fontWeight: "bold",
  }}
>
  {Number(activeModal.payload?.entryFeeSparks || 0) > 0 && (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
    >
      <img
        src="/sparks.png"
        alt="Sparks"
        style={{
          width: "34px",
          height: "34px",
          objectFit: "contain",
          filter: "drop-shadow(1px 1px 2px #000)",
        }}
      />
      <span>{activeModal.payload.entryFeeSparks}</span>
      <span>Sparks</span>
    </div>
  )}

  {Number(activeModal.payload?.entryFeeRunes || 0) > 0 && (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
      }}
    >
      <img
        src="/runes.png"
        alt="Runes"
        style={{
          width: "34px",
          height: "34px",
          objectFit: "contain",
          filter: "drop-shadow(1px 1px 2px #000)",
        }}
      />
      <span>{activeModal.payload.entryFeeRunes}</span>
      <span>Runes</span>
    </div>
  )}
</div>

        <br />

        <span style={{ fontSize: "16px", opacity: 0.9 }}>
          Match every pair before the tomb seals again.
        </span>
      </div></div>

      <div className="modal_footer">
        <button className="game-button green-button" style={{marginRight: 10}}
          onClick={() => {
            closeActiveModal();
            startGame(true);
          }}
        >
          Confirm
        </button>

        <button className="game-button red-button" onClick={closeActiveModal}>
          CANCEL
        </button>
      </div>
    </motion.div>
  </div>
)}

      {activeModal?.type === "INFO" && (
  <SimpleModal open={true} title="Hey!" onClose={closeActiveModal}>
    {activeModal.payload?.message}
  </SimpleModal>
)}

     {activeModal?.type === "WIN" && (
  <SimpleModal
    open={true}
    title={
      <>
        <img
          src="/finger.png"
          alt=""
          style={{
            width: 78,
            height: 78,
            objectFit: "contain",
            display: "block",
            margin: "0 auto 6px",
            filter: "drop-shadow(2px 3px 3px rgba(0,0,0,0.65))",
          }}
        />
        You Win!
      </>
    }
    onClose={closeActiveModal}
    footer={
      <div style={{ display: "flex", gap: 0, justifyContent: "center" }}>
        <button
        style={{marginRight: 10}}
        className="game-button green-button"
          onClick={() => {
            closeActiveModal();
            resetRound();
            startGame(false);
          }}
        >
          PLAY AGAIN
         </button>
        <button className="game-button red-button" onClick={closeActiveModal}>CLOSE</button>
      </div>
    }
  >
    <div style={{ marginTop: 10 }}>
      <div>
        The tomb opened and the treasure is yours.
      </div>

      {activeModal.payload?.reward && (
        <div
          style={{
            marginTop: 14,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          {Number(activeModal.payload.reward?.sparks || 0) > 0 && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                color: "#ffdf5f",
                fontSize: 24,
                fontWeight: 800,
              }}
            >
              <img
                src="/sparks.png"
                alt="Sparks"
                style={{
                  width: 30,
                  height: 30,
                  objectFit: "contain",
                  filter: "drop-shadow(1px 1px 2px #000)",
                }}
              />
              <span>{activeModal.payload.reward.sparks} Sparks</span>
            </div>
          )}

          {Number(activeModal.payload.reward?.runes || 0) > 0 && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                color: "#ffdf5f",
                fontSize: 24,
                fontWeight: 800,
              }}
            >
              <img
                src="/runes.png"
                alt="Runes"
                style={{
                  width: 30,
                  height: 30,
                  objectFit: "contain",
                  filter: "drop-shadow(1px 1px 2px #000)",
                }}
              />
              <span>{activeModal.payload.reward.runes} Runes</span>
            </div>
          )}

          {activeModal.payload.reward?.item && (
            <div
              style={{
                marginTop: 4,
                color: "#ffdf5f",
                fontSize: 22,
                fontWeight: 800,
              }}
            >
              Inventory Item
            </div>
          )}
        </div>
      )}
    </div>
  </SimpleModal>
)}

{starting && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 10000,
      background: "rgba(0,0,0,0.35)",
      backdropFilter: "blur(2px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "auto",
    }}
  >
    <LoadingSpinner text="Opening Memory Tomb" size={64} />
  </div>
)}

      {activeModal?.type === "FAILED" && (
        <SimpleModal
          open={true}
          title="So Close!"
          onClose={closeActiveModal}
          footer={
            <div style={{ display: "flex", gap: 1, justifyContent: "center" }}>
              <button className="game-button green-button" style={{marginRight: 10}}
                onClick={() => {
                  closeActiveModal();
                  resetRound();
                  startGame();
                }}
              >
                Try Again
              </button>
              <button className="game-button red-button" onClick={closeActiveModal}>CLOSE</button>
            </div>
          }
        >
          {activeModal.payload?.message ||
            "The ancient board reset before you solved every pair."}
        </SimpleModal>
      )}
    </div>
  );
}